import { Button, Callout, Intent } from '@blueprintjs/core';
import { IRawTreeNodes, ISynthesis } from '@moleculeone/tree';
import { EndPointProps, StartPointProps } from '@moleculeone/types';
import { SynthesisTree, styled } from '@moleculeone/ui';
import { SearchPreset } from '@retro-ui/api-contract';
import { values } from 'lodash';
import { useState } from 'react';

import ContextToolbox from '@f/components/shared/molecule/ContextToolbox';
import { PriceSidebar } from '@f/components/shared/price-list/PriceSidebar';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';
import { useStartingMaterialPrice } from '@f/hooks/useStartingMaterialPrice';
import { useUrl } from '@f/hooks/useUrl';

import { DebugTreeLabel } from './DebugTreeLabel';
import { PriceTreeButton } from './PriceTreeButton';
import { ReactionConditionsButton } from './ReactionConditionsButton';
import { ReactionConditionsSidebar } from './ReactionConditionsSidebar';
import { ReferenceReactionTreeButton } from './ReferenceReactionTreeButton';

const PriceCallout = styled(Callout, {
  width: 'fit-content',
  margin: '4rem auto',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
});

const isTreeSingleElement = nodes => Object.keys(nodes).length === 1;

const pricePerTierPresent = (node, priceMap, maxTier) => {
  const prices = priceMap[node?.smiles];
  return Array.isArray(prices) && prices.some(item => item.availabilityTier <= maxTier);
};

function PurchaseAvailabilityCallout(props) {
  const { hasRootTieredPrice, onShowPrices } = props;

  return (
    <PriceCallout intent={Intent.SUCCESS} icon={null}>
      The target compound is available
      {hasRootTieredPrice ? (
        <Button
          outlined
          intent={Intent.SUCCESS}
          text="at selected suppliers"
          icon="shopping-cart"
          onClick={onShowPrices}
        />
      ) : (
        <Button
          outlined
          intent={Intent.PRIMARY}
          text="in higher tiers"
          icon="shopping-cart"
          onClick={onShowPrices}
        />
      )}
    </PriceCallout>
  );
}

interface ContextualSynthesisTreeProps {
  tree: IRawTreeNodes;
  searchParams: SearchPreset;
  synthesis: ISynthesis;
}

export function ContextualSynthesisTree(props: ContextualSynthesisTreeProps) {
  const url = useUrl();
  const { tree, searchParams, synthesis } = props;
  const {
    datasets,
    availability_tier: availabilityTier,
    max_price: maxPrice,
    max_price_per_gram: maxPricePerGram,
  } = searchParams;

  const smilesList = values(tree).flatMap(node => ('smiles' in node ? [node.smiles] : []));

  const priceMap = useStartingMaterialPrice({
    smilesList,
    maxPrice,
    maxPricePerGram,
  });

  const isOneNodeTree = isTreeSingleElement(tree);

  const hasRootPrice = pricePerTierPresent(tree[0], priceMap, Infinity);

  const hasRootTieredPrice =
    hasRootPrice && pricePerTierPresent(tree[0], priceMap, availabilityTier);

  const isDebugModeOn = useIsDebugModeOn();

  const [conditionsReactionData, setConditionsReactionData] = useState<
    StartPointProps['reactionData'] | null
  >(null);

  if (!url) {
    return null;
  }

  const getPriceSidebarState = () => {
    const { pricing } = url.read();

    if (pricing == null) {
      return {
        isOpen: false,
        priceItems: [],
      };
    }

    const node = tree[pricing];
    const smiles = 'smiles' in node ? node.smiles : undefined;

    if (!smiles) {
      return {
        isOpen: false,
        priceItems: [],
      };
    }

    return {
      isOpen: true,
      priceItems: priceMap[smiles],
    };
  };

  const onHidePrices = () => {
    const { pricing, ...restSearchParams } = url.read();

    url.write({ pricing: null, ...restSearchParams }, true);
  };

  const onShowPrices = nodeId => {
    const restSearchParams = url.read();

    url.write({ ...restSearchParams, pricing: nodeId }, true);
  };

  return (
    <>
      <SynthesisTree
        tree={tree}
        synthesis={synthesis}
        debugEnabled={isDebugModeOn}
        renderPointContext={{
          common: ReferenceReactionTreeButton,
          start: (props: StartPointProps) => {
            return (
              <ReactionConditionsButton
                {...props}
                onClick={() => {
                  setConditionsReactionData(props.reactionData);
                }}
              />
            );
          },
          end: (props: EndPointProps) => {
            const { substrateId } = props;
            const node = tree[substrateId];
            if ('smiles' in node) {
              const { smiles } = node;
              const hasPrice = (priceMap[smiles] ?? []).length > 0;

              return (
                <PriceTreeButton
                  {...props}
                  smiles={smiles}
                  hasPrice={hasPrice}
                  onCartClick={onShowPrices}
                />
              );
            }
          },
          debug: DebugTreeLabel,
        }}
        renderMoleculeContext={ContextToolbox}
        skipCache={true}
      />

      <PriceSidebar
        {...getPriceSidebarState()}
        selectedDatasets={datasets}
        onClose={onHidePrices}
      />

      <ReactionConditionsSidebar
        isOpen={conditionsReactionData != null}
        onClose={() => setConditionsReactionData(null)}
        reactionData={conditionsReactionData}
      />

      {isOneNodeTree && hasRootPrice && (
        <PurchaseAvailabilityCallout
          hasRootTieredPrice={hasRootTieredPrice}
          onShowPrices={() => onShowPrices(0)}
        />
      )}
    </>
  );
}
